import React, { Fragment, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { InboxIcon } from '@heroicons/react/24/outline';
import { CHANNEL_TYPE_LABELS, ChannelType } from '../../../types/atendimentoEnums';
import { Inbox } from '../../../types/inbox';
import { ChannelTypeIcon } from '../ChannelTypeIcon';
import { WorkspaceThemeClasses } from './workspaceTheme';

export type WorkspaceInboxFilterValue =
  | { kind: 'all' }
  | { kind: 'channel'; channelType: ChannelType }
  | { kind: 'inbox'; inboxId: string };

interface WorkspaceInboxFilterProps {
  theme: WorkspaceThemeClasses;
  inboxes: Inbox[];
  value: WorkspaceInboxFilterValue;
  onChange: (value: WorkspaceInboxFilterValue) => void;
}

type FilterOption =
  | { key: 'all'; kind: 'all'; label: string }
  | { key: `channel-${ChannelType}`; kind: 'channel'; channelType: ChannelType; label: string }
  | { key: `inbox-${string}`; kind: 'inbox'; inboxId: string; label: string; channelType: ChannelType };

const optionKeyFor = (value: WorkspaceInboxFilterValue): string => {
  if (value.kind === 'all')
  {
    return 'all';
  }

  if (value.kind === 'channel')
  {
    return `channel-${value.channelType}`;
  }

  return `inbox-${value.inboxId}`;
};

const optionLabelFor = (value: WorkspaceInboxFilterValue, inboxes: Inbox[]): string => {
  if (value.kind === 'all')
  {
    return 'Todas as caixas';
  }

  if (value.kind === 'channel')
  {
    return `Todos — ${CHANNEL_TYPE_LABELS[value.channelType]}`;
  }

  const inbox = inboxes.find((item) => item.id === value.inboxId);
  return inbox?.name ?? 'Caixa selecionada';
};

const buttonLabelFor = (value: WorkspaceInboxFilterValue, inboxes: Inbox[]): string => {
  if (value.kind === 'all')
  {
    return 'Ver todas as caixas';
  }

  return `Caixa: ${optionLabelFor(value, inboxes)}`;
};

export const WorkspaceInboxFilter: React.FC<WorkspaceInboxFilterProps> = ({
  theme,
  inboxes,
  value,
  onChange,
}) => {
  const options = useMemo(() => {
    const grouped = new Map<ChannelType, Inbox[]>();

    inboxes.forEach((inbox) => {
      const current = grouped.get(inbox.channelType) ?? [];
      current.push(inbox);
      grouped.set(inbox.channelType, current);
    });

    const result: FilterOption[] = [
      { key: 'all', kind: 'all', label: 'Todas as caixas' },
    ];

    Array.from(grouped.entries())
      .sort(([left], [right]) => CHANNEL_TYPE_LABELS[left].localeCompare(CHANNEL_TYPE_LABELS[right], 'pt-BR'))
      .forEach(([channelType, channelInboxes]) => {
        result.push({
          key: `channel-${channelType}`,
          kind: 'channel',
          channelType,
          label: `Todos — ${CHANNEL_TYPE_LABELS[channelType]}`,
        });

        channelInboxes
          .slice()
          .sort((left, right) => left.name.localeCompare(right.name, 'pt-BR'))
          .forEach((inbox) => {
            result.push({
              key: `inbox-${inbox.id}`,
              kind: 'inbox',
              inboxId: inbox.id,
              channelType: inbox.channelType,
              label: inbox.name,
            });
          });
      });

    return result;
  }, [inboxes]);

  const selectedKey = optionKeyFor(value);
  const buttonLabel = buttonLabelFor(value, inboxes);

  const handleSelect = (key: string) => {
    const option = options.find((item) => item.key === key);
    if (!option)
    {
      return;
    }

    if (option.kind === 'all')
    {
      onChange({ kind: 'all' });
      return;
    }

    if (option.kind === 'channel')
    {
      onChange({ kind: 'channel', channelType: option.channelType });
      return;
    }

    onChange({ kind: 'inbox', inboxId: option.inboxId });
  };

  const selectedChannelType =
    value.kind === 'channel'
      ? value.channelType
      : value.kind === 'inbox'
        ? inboxes.find((item) => item.id === value.inboxId)?.channelType
        : undefined;

  return (
    <div className="min-w-0">
      <Listbox value={selectedKey} onChange={handleSelect}>
        <div className="relative">
          <Listbox.Button
            aria-label="Filtrar por caixa"
            className={`relative box-border flex h-9 max-h-9 min-h-9 w-full cursor-pointer items-center overflow-hidden rounded-md border px-2 py-0 pr-7 text-left text-[11px] leading-none transition-colors focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${theme.input}`}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              {value.kind === 'all' ? (
                <InboxIcon className={`h-3.5 w-3.5 flex-shrink-0 ${theme.faint}`} aria-hidden="true" />
              ) : selectedChannelType ? (
                <ChannelTypeIcon channelType={selectedChannelType} className="h-3.5 w-3.5 flex-shrink-0" />
              ) : (
                <InboxIcon className={`h-3.5 w-3.5 flex-shrink-0 ${theme.faint}`} aria-hidden="true" />
              )}
              <span className={`whitespace-nowrap font-medium ${theme.text}`}>{buttonLabel}</span>
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
              <ChevronUpDownIcon className={`h-4 w-4 ${theme.faint}`} aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              className={`absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border py-1 text-xs shadow-lg focus:outline-none ${theme.border} ${theme.surface}`}
            >
              {options.map((option, index) => {
                const isChannelGroup = option.kind === 'channel';
                const isInboxItem = option.kind === 'inbox';
                const showGroupDivider = isChannelGroup && index > 1;

                return (
                  <Listbox.Option key={option.key} value={option.key} as={Fragment}>
                    {({ active, selected }) => (
                      <li
                        className={`relative flex cursor-pointer select-none items-center gap-2 px-3 py-2 ${
                          active ? theme.selectedRow : ''
                        } ${isInboxItem ? 'pl-9' : ''} ${showGroupDivider ? `border-t ${theme.border}` : ''}`}
                      >
                        {option.kind === 'all' ? (
                          <InboxIcon className={`h-4 w-4 flex-shrink-0 ${theme.faint}`} aria-hidden="true" />
                        ) : isChannelGroup ? (
                          <ChannelTypeIcon channelType={option.channelType} className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <span
                            className={`h-1 w-1 flex-shrink-0 rounded-full ${theme.faint} bg-current`}
                            aria-hidden="true"
                          />
                        )}
                        <span
                          className={`flex-1 truncate ${
                            selected ? 'font-semibold' : isChannelGroup ? 'font-medium' : 'font-normal'
                          } ${theme.text}`}
                        >
                          {option.label}
                        </span>
                        {selected && (
                          <CheckIcon className="h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
                        )}
                      </li>
                    )}
                  </Listbox.Option>
                );
              })}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};
